import { TiktokUserImage, InstagramUserImage } from '@round/api';
import { influencer } from '@round/api';

// Type definitions for platform users
type TiktokPlatformUser = NonNullable<influencer.CreatorSearchResult['platforms']['tiktok']>[0];
type InstagramPlatformUser = NonNullable<influencer.CreatorSearchResult['platforms']['instagram']>[0];

// Utility function to extract unique user IDs from creator search data
export const extractUserIdsFromData = (data: influencer.CreatorSearchResult[]) => {
    const tiktokUserIds: number[] = [];
    const instagramUserIds: number[] = [];

    data.forEach((item) => {
        if (item.platforms.tiktok && item.platforms.tiktok.length > 0) {
            item.platforms.tiktok.forEach((tiktokUser: TiktokPlatformUser) => {
                if (tiktokUser.creatorbase_user_id) {
                    tiktokUserIds.push(tiktokUser.creatorbase_user_id);
                }
            });
        }
        if (item.platforms.instagram && item.platforms.instagram.length > 0) {
            item.platforms.instagram.forEach((instagramUser: InstagramPlatformUser) => {
                if (instagramUser.creatorbase_user_id) {
                    instagramUserIds.push(instagramUser.creatorbase_user_id);
                }
            });
        }
    });

    return { tiktokUserIds, instagramUserIds };
};

// Utility function to get the best user image with TikTok priority
export const getUserImage = (
    userId: number,
    tiktokUserImages: TiktokUserImage[],
    instagramUserImages: InstagramUserImage[]
): string | null => {
    // First try to find TikTok image (priority)
    const tiktokImage = tiktokUserImages.find((image) => image.user_id === userId);

    if (tiktokImage) {
        return tiktokImage.avatar_thumb.cached_url || tiktokImage.avatar_thumb.original_url;
    }

    // Fallback to Instagram image
    const instagramImage = instagramUserImages.find((image) => image.user_id === userId);
    if (instagramImage) {
        return instagramImage.avatar_thumb.cached_url || instagramImage.avatar_thumb.original_url;
    }

    return null;
};

// Utility function to transform data with user images
export const transformDataWithUserImages = (
    data: influencer.CreatorSearchResult[],
    tiktokUserImages: TiktokUserImage[],
    instagramUserImages: InstagramUserImage[]
) => {
    return data.map((item) => ({
        ...item,
        // Add user image property that can be used in columns
        userImage: (() => {
            // First try to find TikTok user image
            if (item.platforms.tiktok && item.platforms.tiktok.length > 0) {
                const tiktokUser = item.platforms.tiktok[0];
                if (tiktokUser.creatorbase_user_id) {
                    const image = getUserImage(tiktokUser.creatorbase_user_id, tiktokUserImages, instagramUserImages);
                    if (image) return image;
                }
            }

            // Fallback to Instagram user image
            if (item.platforms.instagram && item.platforms.instagram.length > 0) {
                const instagramUser = item.platforms.instagram[0];
                if (instagramUser.creatorbase_user_id) {
                    const image = getUserImage(
                        instagramUser.creatorbase_user_id,
                        tiktokUserImages,
                        instagramUserImages
                    );
                    if (image) return image;
                }
            }

            return null;
        })(),
    }));
};
